import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import ListIcon from "@mui/icons-material/List";
import InfoIcon from "@mui/icons-material/Info";
import AgreementFilters from "./AgreementFilter";
import AgreementTable from "./AgreementTable";
import { getPublicAgreements } from "../../services/agreementServices";
import { STATUS_DEFAULT, STATUS_OPTIONS_PUBLIC } from "../../constants/status";
import { useDebounce } from "../../hooks/useDebounce";
import PublicAgreementShow from "./PublicAgreementShow";
import { exportAgreements } from "../../services/agreementServices";
import { generatePublicAgreementsToPdf } from "../../pdfs/generatePublicAgreementsToPdf";

const agreementsPerPage = 10;

export default function PublicAgreementList() {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_DEFAULT);
  const [subscriptionFrom, setSubscriptionFrom] = useState(null);
  const [subscriptionTo, setSubscriptionTo] = useState(null);
  const [validityFrom, setValidityFrom] = useState(null);
  const [validityTo, setValidityTo] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedAgreementId, setSelectedAgreementId] = useState(null);

  const debouncedQuery = useDebounce(searchQuery, 300);

  const filters = useMemo(
    () => ({
      q: debouncedQuery,
      status: statusFilter,
      type: typeFilter,
      subscription_from: subscriptionFrom?.format("YYYY-MM-DD"),
      subscription_to: subscriptionTo?.format("YYYY-MM-DD"),
      validity_from: validityFrom?.format("YYYY-MM-DD"),
      validity_to: validityTo?.format("YYYY-MM-DD"),
      page,
    }),
    [
      debouncedQuery,
      statusFilter,
      typeFilter,
      subscriptionFrom,
      subscriptionTo,
      validityFrom,
      validityTo,
      page,
    ]
  );

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handleTypeChange = (newType) => {
    setTypeFilter(newType);
    setPage(1);
  };

  const handleSubscriptionFromChange = (newDate) => {
    setSubscriptionFrom(newDate);
    setPage(1);
  };

  const handleSubscriptionToChange = (newDate) => {
    setSubscriptionTo(newDate);
    setPage(1);
  };

  const handleValidityFromChange = (newDate) => {
    setValidityFrom(newDate);
    setPage(1);
  };

  const handleValidityToChange = (newDate) => {
    setValidityTo(newDate);
    setPage(1);
  };

  const handleSearchChange = (newSearch) => {
    setSearchQuery(newSearch);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const loadAgreements = async () => {
    setIsLoading(true);
    try {
      const response = await getPublicAgreements(filters);
      
      setAgreements(response.data.data.agreements);
      setTotalCount(response.data.data.total_count);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Error al cargar los convenios públicos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAgreements();
  }, [filters]);

  const handleExportPublic = async () => {
    try {
      const response = await exportAgreements(filters, "public");
      
      await generatePublicAgreementsToPdf(response.data.data.agreements);
    } catch (err) {
      console.error("Error al exportar PDF:", err);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setTypeFilter("");
    setSubscriptionFrom(null);
    setSubscriptionTo(null);
    setValidityFrom(null);
    setValidityTo(null);
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        background: `linear-gradient(to bottom, ${theme.palette.primary.main}, #d75d2300)`,
        p: 4,
      })}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<LoginIcon />}
          onClick={() => navigate("/login")}
          sx={{
            color: "#fff",
            borderColor: "#fff",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
              borderColor: "#fff",
            },
          }}
        >
          Iniciar sesión
        </Button>
      </Box>

      <Typography
        variant="h4"
        fontWeight="bold"
        align="center"
        color="white"
        sx={{ textShadow: "1px 1px 2px #00000033" }}
      >
        UNIVERSIDAD NACIONAL DE ITAPÚA
      </Typography>
      <Typography
        variant="h6"
        align="center"
        fontWeight="bold"
        color="white"
        sx={{ mb: 4 }}
      >
        CONVENIOS APROBADOS
      </Typography>

      <Tabs
        value={tabIndex}
        onChange={(e, v) => setTabIndex(v)}
        textColor="inherit"
        TabIndicatorProps={{
          style: {
            backgroundColor: "white", // indicador blanco
          },
        }}
      >
        <Tab
          icon={<ListIcon />}
          label="Lista de convenios"
          sx={{
            color: tabIndex === 0 ? "white" : "#cccccc", // blanco activo, gris claro inactivo
            fontWeight: "bold",
          }}
        />
        {selectedAgreementId && (
          <Tab
            icon={<InfoIcon />}
            label="Detalle del convenio"
            sx={{
              color: tabIndex === 1 ? "white" : "#cccccc",
              fontWeight: "bold",
            }}
          />
        )}
      </Tabs>

      {tabIndex === 0 && (
        <>
          <AgreementFilters
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            statusFilter={statusFilter}
            onStatusChange={handleStatusChange}
            subscriptionFrom={subscriptionFrom}
            onSubscriptionFromChange={handleSubscriptionFromChange}
            subscriptionTo={subscriptionTo}
            onSubscriptionToChange={handleSubscriptionToChange}
            validityFrom={validityFrom}
            onValidityFromChange={handleValidityFromChange}
            validityTo={validityTo}
            onValidityToChange={handleValidityToChange}
            typeFilter={typeFilter}
            onTypeChange={handleTypeChange}
            onClearFilters={clearFilters}
            statusOptions={STATUS_OPTIONS_PUBLIC}
          />
          <Button onClick={handleExportPublic} variant="contained" sx={{ mt: 2 }}>
            Exportar a PDF
          </Button>
          {isLoading ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : error ? (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          ) : (
            <AgreementTable
              agreements={agreements}
              page={page}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              setSelectedAgreementId={setSelectedAgreementId}
              setTabIndex={setTabIndex}
              showActions={false} 
            />
          )}{" "}
        </>
      )}

      {tabIndex === 1 && selectedAgreementId && (
        <Box>
          <PublicAgreementShow id={selectedAgreementId} />
        </Box>
      )}
    </Box>
  );
}
